"use client";

import * as React from "react";
import { motion } from "framer-motion";

interface AstroMascotProps {
  expression?: "waving" | "happy" | "curious" | "proud" | "excited" | "confused" | "helpful" | "professional";
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}

export function AstroMascot({ 
  expression = "waving", 
  size = "md", 
  className = "" 
}: AstroMascotProps) {
  const sizeClasses = {
    sm: "w-16 h-16",
    md: "w-24 h-24", 
    lg: "w-32 h-32",
    xl: "w-48 h-48"
  };

  const waveAnimation = {
    initial: { rotate: 0 },
    animate: { 
      rotate: [0, 15, -15, 15, 0],
      transition: { 
        duration: 2,
        repeat: Infinity,
        repeatDelay: 3,
        ease: "easeInOut"
      }
    }
  };

  const floatAnimation = {
    initial: { y: 0 },
    animate: { 
      y: [-2, 2, -2],
      transition: { 
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <motion.div 
      className={`${sizeClasses[size]} ${className} relative`}
      variants={floatAnimation}
      initial="initial"
      animate="animate"
    >
      <svg
        viewBox="0 0 200 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full drop-shadow-lg"
      >
        {/* Background glow */}
        <defs>
          <radialGradient id="glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(139, 92, 246, 0.1)" />
            <stop offset="100%" stopColor="rgba(139, 92, 246, 0)" />
          </radialGradient>
          <linearGradient id="helmetGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f8fafc" />
            <stop offset="100%" stopColor="#e2e8f0" />
          </linearGradient>
          <linearGradient id="suitGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#e2e8f0" />
            <stop offset="100%" stopColor="#cbd5e1" />
          </linearGradient>
          <linearGradient id="visorGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(59, 130, 246, 0.8)" />
            <stop offset="100%" stopColor="rgba(99, 102, 241, 0.6)" />
          </linearGradient>
        </defs>

        {/* Background glow */}
        <circle cx="100" cy="100" r="90" fill="url(#glow)" />

        {/* Body (space suit) */}
        <ellipse cx="100" cy="140" rx="45" ry="35" fill="url(#suitGradient)" />
        
        {/* Arms */}
        <ellipse cx="65" cy="125" rx="12" ry="25" fill="url(#suitGradient)" transform="rotate(-20 65 125)" />
        <motion.ellipse 
          cx="135" 
          cy="125" 
          rx="12" 
          ry="25" 
          fill="url(#suitGradient)" 
          transform="rotate(20 135 125)"
          variants={expression === "waving" ? waveAnimation : {}}
          initial="initial"
          animate="animate"
          style={{ transformOrigin: "135px 125px" }}
        />
        
        {/* Hands */}
        <circle cx="62" cy="105" r="8" fill="url(#suitGradient)" />
        <motion.circle 
          cx="138" 
          cy="105" 
          r="8" 
          fill="url(#suitGradient)"
          variants={expression === "waving" ? waveAnimation : {}}
          initial="initial"
          animate="animate"
          style={{ transformOrigin: "138px 105px" }}
        />

        {/* Chest panel */}
        <rect x="85" y="125" width="30" height="20" rx="8" fill="#64748b" />
        <rect x="90" y="130" width="20" height="3" rx="1.5" fill="#3b82f6" />
        <rect x="90" y="135" width="15" height="3" rx="1.5" fill="#10b981" />
        <circle cx="105" cy="140" r="2" fill="#ef4444" />

        {/* Helmet */}
        <circle cx="100" cy="85" r="40" fill="url(#helmetGradient)" stroke="#cbd5e1" strokeWidth="2" />
        
        {/* Visor */}
        <circle cx="100" cy="85" r="35" fill="url(#visorGradient)" opacity="0.7" />
        
        {/* Face inside helmet */}
        <circle cx="100" cy="85" r="30" fill="#fef3c7" />
        
        {/* Eyes - different shapes based on expression */}
        {expression === "confused" ? (
          <>
            {/* Squinted/puzzled eyes */}
            <path d="M 86 80 Q 90 76 94 80" stroke="#1f2937" strokeWidth="2" fill="none" strokeLinecap="round" />
            <path d="M 106 80 Q 110 76 114 80" stroke="#1f2937" strokeWidth="2" fill="none" strokeLinecap="round" />
            {/* Question mark above head */}
            <path d="M 85 65 Q 85 60 90 60 Q 95 60 95 65 Q 95 68 92 70" stroke="#64748b" strokeWidth="1.5" fill="none" strokeLinecap="round" />
            <circle cx="92" cy="72" r="1" fill="#64748b" />
          </>
        ) : expression === "professional" ? (
          <>
            {/* Focused, confident eyes */}
            <ellipse cx="90" cy="80" rx="3.5" ry="5" fill="#1f2937" />
            <ellipse cx="110" cy="80" rx="3.5" ry="5" fill="#1f2937" />
            <ellipse cx="91" cy="78" rx="1" ry="1.5" fill="#ffffff" />
            <ellipse cx="111" cy="78" rx="1" ry="1.5" fill="#ffffff" />
            {/* Professional eyebrows */}
            <path d="M 86 75 Q 90 73 94 75" stroke="#1f2937" strokeWidth="1.5" fill="none" strokeLinecap="round" />
            <path d="M 106 75 Q 110 73 114 75" stroke="#1f2937" strokeWidth="1.5" fill="none" strokeLinecap="round" />
          </>
        ) : (
          <>
            <ellipse cx="90" cy="80" rx="4" ry="6" fill="#1f2937" />
            <ellipse cx="110" cy="80" rx="4" ry="6" fill="#1f2937" />
            <ellipse cx="91" cy="78" rx="1.5" ry="2" fill="#ffffff" />
            <ellipse cx="111" cy="78" rx="1.5" ry="2" fill="#ffffff" />
          </>
        )}
        
        {/* Mouth based on expression */}
        {expression === "waving" || expression === "happy" || expression === "helpful" ? (
          <path d="M 90 95 Q 100 105 110 95" stroke="#1f2937" strokeWidth="2" fill="none" strokeLinecap="round" />
        ) : expression === "curious" ? (
          <circle cx="100" cy="95" r="3" fill="#1f2937" />
        ) : expression === "excited" ? (
          <ellipse cx="100" cy="95" rx="8" ry="6" fill="#1f2937" />
        ) : expression === "confused" ? (
          <path d="M 90 95 Q 100 90 110 95" stroke="#1f2937" strokeWidth="2" fill="none" strokeLinecap="round" />
        ) : expression === "professional" ? (
          <path d="M 92 95 Q 100 100 108 95" stroke="#1f2937" strokeWidth="2" fill="none" strokeLinecap="round" />
        ) : (
          <path d="M 90 95 Q 100 100 110 95" stroke="#1f2937" strokeWidth="2" fill="none" strokeLinecap="round" />
        )}
        
        {/* Helmet reflection */}
        <path 
          d="M 75 65 Q 85 55 95 65 Q 105 70 115 65" 
          stroke="rgba(255,255,255,0.4)" 
          strokeWidth="3" 
          fill="none" 
          strokeLinecap="round" 
        />
        
        {/* Antenna */}
        <line x1="100" y1="45" x2="100" y2="35" stroke="#64748b" strokeWidth="2" />
        <circle cx="100" cy="32" r="3" fill="#ef4444" />
        <motion.circle 
          cx="100" 
          cy="32" 
          r="3" 
          fill="#ef4444" 
          opacity="0.5"
          animate={{ scale: [1, 1.5, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        />

        {/* Badge/Clipboard */}
        <rect x="70" y="140" width="12" height="15" rx="2" fill="#ffffff" stroke="#cbd5e1" strokeWidth="1" />
        <line x1="72" y1="145" x2="80" y2="145" stroke="#64748b" strokeWidth="1" />
        <line x1="72" y1="148" x2="78" y2="148" stroke="#64748b" strokeWidth="1" />
        <line x1="72" y1="151" x2="80" y2="151" stroke="#64748b" strokeWidth="1" />

        {/* Stars around Astro */}
        <motion.g
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: "100px 100px" }}
        >
          <g fill="#fbbf24" opacity="0.6">
            <polygon points="40,60 42,66 48,66 43,70 45,76 40,72 35,76 37,70 32,66 38,66" />
            <polygon points="160,50 162,56 168,56 163,60 165,66 160,62 155,66 157,60 152,56 158,56" />
            <polygon points="170,120 172,126 178,126 173,130 175,136 170,132 165,136 167,130 162,126 168,126" />
            <polygon points="30,140 32,146 38,146 33,150 35,156 30,152 25,156 27,150 22,146 28,146" />
          </g>
        </motion.g>
      </svg>
    </motion.div>
  );
}